import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Menu, X, Globe, ChevronDown, Home, Info, Package, Calculator, Ship, Image, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { language, changeLanguage } = useLanguage();
  const t = translations[language];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const currentLang = languages.find(l => l.code === language);

  return (
    <header className="fixed top-0 w-full bg-[#0a1929] border-b border-white/5 backdrop-blur-md z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <div
              className="luxury-logo"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              data-testid="header-logo-wrapper"
            >
              <img
                src="/cozey-logo.jpg"
                alt="Cozey Yildiz Export L.L.C"
                className="luxury-logo-img"
                loading="eager"
                decoding="async"
                data-testid="header-logo"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-home">
              {t.nav.home}
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-about">
              {t.nav.about}
            </button>
            <button onClick={() => scrollToSection('services')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-services">
              {t.nav.services}
            </button>
            <button onClick={() => scrollToSection('quote')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-quote">
              {t.nav.quote}
            </button>
            <button onClick={() => scrollToSection('tracking')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-tracking">
              {t.nav.tracking}
            </button>
            <button onClick={() => scrollToSection('gallery')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-gallery">
              {t.nav.gallery}
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-200 hover:text-white transition-colors font-medium text-sm" data-testid="nav-contact">
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Dropdown & Mobile Menu */}
          <div className="flex items-center space-x-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2 bg-transparent border-white/20 text-gray-200 hover:bg-white/10 hover:text-white hover:border-white/30"
                  data-testid="language-toggle"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">{currentLang?.name}</span>
                  <span className="sm:hidden">{currentLang?.flag}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-blue-50 font-semibold' : ''}`}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-200 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - rendered via portal so backdrop escapes header's backdrop-filter containing block */}
        {mobileMenuOpen && createPortal(
          <>
            {/* Backdrop */}
            <div
              className="lg:hidden fixed left-0 right-0 bottom-0 bg-black/50 backdrop-blur-sm z-[60] animate-in fade-in duration-200"
              style={{ top: '5rem' }}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            {/* Menu panel */}
            <nav
              className="lg:hidden fixed left-0 right-0 top-20 bg-[#0a1929] border-t border-white/10 shadow-2xl z-[61] max-h-[calc(100vh-5rem)] overflow-y-auto"
              style={{ animation: 'slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              <div className="container mx-auto px-4 py-3 space-y-1">
                <MobileNavLink icon={Home} label={t.nav.home} onClick={() => scrollToSection('home')} testId="mobile-nav-home" />
                <MobileNavLink icon={Info} label={t.nav.about} onClick={() => scrollToSection('about')} testId="mobile-nav-about" />
                <MobileNavLink icon={Package} label={t.nav.services} onClick={() => scrollToSection('services')} testId="mobile-nav-services" />
                <MobileNavLink icon={Calculator} label={t.nav.quote} onClick={() => scrollToSection('quote')} testId="mobile-nav-quote" highlight />
                <MobileNavLink icon={Ship} label={t.nav.tracking} onClick={() => scrollToSection('tracking')} testId="mobile-nav-tracking" />
                <MobileNavLink icon={Image} label={t.nav.gallery} onClick={() => scrollToSection('gallery')} testId="mobile-nav-gallery" />
                <MobileNavLink icon={MessageCircle} label={t.nav.contact} onClick={() => scrollToSection('contact')} testId="mobile-nav-contact" />
              </div>
            </nav>
          </>,
          document.body
        )}
      </div>
    </header>
  );
};

export default Header;

// Mobile nav link with icon, generous padding, active-state ready
const MobileNavLink = ({ icon: Icon, label, onClick, testId, highlight }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-left transition-all active:scale-[0.98] ${
      highlight
        ? 'bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/15'
        : 'text-gray-200 hover:bg-white/10 hover:text-white border border-transparent'
    }`}
    style={{ minHeight: '52px' }}
  >
    <Icon className={`h-5 w-5 flex-shrink-0 ${highlight ? 'text-[#d4af37]' : 'text-gray-400'}`} />
    <span className="font-medium text-base">{label}</span>
  </button>
);
